import React, { useEffect } from 'react';

interface HelmetProps {
    children?: React.ReactNode;
}

export const Helmet: React.FC<HelmetProps> = ({ children }) => {
    useEffect(() => {
        const managedElements: Element[] = [];

        React.Children.forEach(children, (child) => {
            if (!React.isValidElement(child)) return;

            // Handle <title>
            if (child.type === 'title') {
                const titleText = typeof child.props.children === 'string'
                    ? child.props.children
                    : Array.isArray(child.props.children)
                        ? child.props.children.join('')
                        : '';
                if (titleText) {
                    document.title = titleText;
                }
            }
            // Handle <meta>
            else if (child.type === 'meta') {
                const { name, property, httpEquiv, content } = child.props;
                let selector = '';
                if (name) selector = `meta[name="${name}"]`;
                else if (property) selector = `meta[property="${property}"]`;
                else if (httpEquiv) selector = `meta[http-equiv="${httpEquiv}"]`;

                if (selector) {
                    let metaTag = document.head.querySelector(selector);
                    if (metaTag) {
                        metaTag.setAttribute('content', content || '');
                    } else {
                        metaTag = document.createElement('meta');
                        if (name) metaTag.setAttribute('name', name);
                        if (property) metaTag.setAttribute('property', property);
                        if (httpEquiv) metaTag.setAttribute('http-equiv', httpEquiv);
                        metaTag.setAttribute('content', content || '');
                        document.head.appendChild(metaTag);
                        managedElements.push(metaTag);
                    }
                }
            }
            // Handle <link>
            else if (child.type === 'link') {
                const { rel, href } = child.props;
                if (rel && href) {
                    let linkTag = document.head.querySelector(`link[rel="${rel}"]`);
                    if (linkTag) {
                        linkTag.setAttribute('href', href);
                    } else {
                        linkTag = document.createElement('link');
                        linkTag.setAttribute('rel', rel);
                        linkTag.setAttribute('href', href);
                        document.head.appendChild(linkTag);
                        managedElements.push(linkTag);
                    }
                }
            }
        });

        return () => {
            managedElements.forEach((el) => {
                if (el.parentNode) {
                    el.parentNode.removeChild(el);
                }
            });
        };
    }, [children]);

    return null;
};

export default Helmet;
